import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { Layout, spacing, Text, useViewportSize } from '@lemonade-hq/uniclient-theme';
import isObject from 'lodash/isObject';
import type { MotionNodeOptions } from 'motion/react';
import { AnimatePresence, motion } from 'motion/react';
import { WIZARD_EXPLANATION_BUBBLE_ELEMENT_ID } from '../utils/consts';
import type { InterpolableTextComponentProps } from './InterpolableText';
import { InterpolableText } from './InterpolableText';
import * as styles from './WizardExplanationBubble.css';

const LONG_ANIMATION_DURATION: Milliseconds = 0.5;
const QUICK_ANIMATION_DURATION: Milliseconds = 0.2;
const MOBILE_HEIGHT_OFFSET_ANIMATION = 10;

const mobileAnimation: MotionNodeOptions = {
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: MOBILE_HEIGHT_OFFSET_ANIMATION },
  initial: { opacity: 0, y: MOBILE_HEIGHT_OFFSET_ANIMATION },
  transition: { duration: QUICK_ANIMATION_DURATION, ease: 'easeOut' },
};

const desktopAnimation: MotionNodeOptions = {
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: { duration: QUICK_ANIMATION_DURATION, ease: 'easeInOut' } },
  initial: { opacity: 0 },
  transition: { duration: LONG_ANIMATION_DURATION, ease: 'easeInOut' },
};

type ExplanationBubbleProps = Partial<InterpolableTextComponentProps>;

export const ExplanationBubble: React.FC<ExplanationBubbleProps> = ({ text, onAction, answers }) => {
  const { isMobileViewport: isMobile } = useViewportSize();
  const animation = isMobile ? mobileAnimation : desktopAnimation;
  const animatePresenceMode = isMobile ? 'sync' : 'popLayout';
  const shouldRenderText = text != null && (isObject(text) && 'text' in text ? text.text : text).length > 0;

  return (
    <Layout data-animated>
      <AnimatePresence mode={animatePresenceMode}>
        {shouldRenderText && (
          <Layout
            as={motion.div}
            {...animation}
            className={styles.explanationBubble}
            id={WIZARD_EXPLANATION_BUBBLE_ELEMENT_ID}
            padding={`${spacing.s12} ${spacing.s16}`}
            position="relative"
          >
            <Text type="bodyMedium">
              <InterpolableText answers={answers} onAction={onAction} text={text} />
            </Text>
          </Layout>
        )}
      </AnimatePresence>
    </Layout>
  );
};
